import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Role } from "@/data/role-defaults";

/* Phase 6 TweaksPanel — Sheet-based.

   Listens for harness postMessage events and toggles the Sheet
   accordingly. Matches legacy/tweaks-panel.jsx:220-222 exactly:
   the handler reads only `e.data.type` and ignores any payload.

   The only tweak currently wired is `role` (matches the EDITMODE
   block in index.html / standalone.html). Reset clears localStorage
   via `resetTweaks` from useTweaks → re-hydrate from EDITMODE → hardcoded.

   Sheet trigger is implicit: the panel only renders when the harness
   has activated edit mode, so there is no visible toggle button.
*/

interface TweaksPanelProps {
  role: Role;
  setTweak: <K extends "role">(key: K, value: string) => void;
  resetTweaks?: () => void;
}

const ROLE_OPTIONS: Array<{ value: Role; label: string }> = [
  { value: "programme", label: "Programme owner" },
  { value: "district", label: "District lead" },
  { value: "school", label: "School leader" },
  { value: "teacher", label: "Teacher" },
];

export function TweaksPanel({ role, setTweak, resetTweaks }: TweaksPanelProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const t = (e.data as { type?: string } | undefined)?.type;
      if (t === "__activate_edit_mode") setOpen(true);
      else if (t === "__deactivate_edit_mode") setOpen(false);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Mirror legacy dismiss: notify harness toolbar to flip off
      try {
        window.parent?.postMessage({ type: "__edit_mode_dismissed" }, "*");
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        data-testid="tweaks-panel"
        className="w-80 sm:max-w-sm"
      >
        <SheetHeader>
          <SheetTitle>Tweaks</SheetTitle>
          <SheetDescription>
            Preview the portal as a different role. Changes persist
            locally; the harness can override defaults.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-4 px-4">
          <Separator />

          <div className="flex flex-col gap-2">
            <label
              htmlFor="tweaks-role"
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Sign in as
            </label>
            <Select
              value={role}
              onValueChange={(v) => setTweak("role", v as Role)}
            >
              <SelectTrigger id="tweaks-role" data-testid="tweaks-role-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    data-testid={`tweaks-role-option-${opt.value}`}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Reset
            </span>
            <Button
              type="button"
              variant="outline"
              data-testid="tweaks-reset"
              onClick={() => {
                if (resetTweaks) resetTweaks();
              }}
            >
              Reset to defaults
            </Button>
            <p className="text-xs text-muted-foreground">
              Clears local overrides. Re-hydrates from the EDITMODE
              block, then hardcoded defaults.
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
